import axios from '@/service/request';
import type { AxiosRequestConfig } from 'axios';


// ---------------------------------------------------------------------------
// UserCouponStatusEnum：优惠券使用状态（与后端枚举一致）
// ---------------------------------------------------------------------------
export const UserCouponStatusEnum = {
  /** 未使用 */
  UNUSED: '0',
  /** 已使用 */
  USED: '1',
  /** 已过期 */
  EXPIRED: '2',
} as const

export type UserCouponStatusEnumCode =
  (typeof UserCouponStatusEnum)[keyof typeof UserCouponStatusEnum]

/**
 * 状态码 → 多语言 key（与 `src/i18n/langs/*.json` 一致）
 * 展示：`t(USER_COUPON_STATUS_LABEL[code])` 或 `<LangText lngKey={USER_COUPON_STATUS_LABEL[code]} />`
 */
export const USER_COUPON_STATUS_LABEL: Record<UserCouponStatusEnumCode, string> = {
  [UserCouponStatusEnum.UNUSED]: 'f9b74618',
  [UserCouponStatusEnum.USED]: '54188f2e',
  [UserCouponStatusEnum.EXPIRED]: 'db690fa9',
}

// ---------------------------------------------------------------------------
// CompanyBusinessTypeEnum：企业经营类型（与后端枚举一致）
// ---------------------------------------------------------------------------
export const CompanyBusinessTypeEnum = {
  DIGITAL_ELECTRONICS: 'DIGITAL_ELECTRONICS',
  FASHION_APPAREL: 'FASHION_APPAREL',
  FOOD_DINING: 'FOOD_DINING',
  BEAUTY_SKINCARE: 'BEAUTY_SKINCARE',
  TRAFFIC_TRAVEL: 'TRAFFIC_TRAVEL',
  ACCOMMODATION_ENTERTAINMENT: 'ACCOMMODATION_ENTERTAINMENT',
} as const

export type CompanyBusinessTypeEnumCode =
  (typeof CompanyBusinessTypeEnum)[keyof typeof CompanyBusinessTypeEnum]

/**
 * 类型编码 → 多语言 key（与领券中心 Tab 等一致：`a4429b2b`～`92b0cd80`）
 * 展示：`t(COMPANY_BUSINESS_TYPE_LABEL[code])` 或 `<LangText lngKey={...} />`
 */
export const COMPANY_BUSINESS_TYPE_LABEL: Record<CompanyBusinessTypeEnumCode, string> = {
  [CompanyBusinessTypeEnum.DIGITAL_ELECTRONICS]: 'a4429b2b',
  [CompanyBusinessTypeEnum.FASHION_APPAREL]: '71bdcd98',
  [CompanyBusinessTypeEnum.FOOD_DINING]: '7c29670d',
  [CompanyBusinessTypeEnum.BEAUTY_SKINCARE]: '4bae91f8',
  [CompanyBusinessTypeEnum.TRAFFIC_TRAVEL]: 'c3d567d5',
  [CompanyBusinessTypeEnum.ACCOMMODATION_ENTERTAINMENT]: '92b0cd80',
}

// ---------------------------------------------------------------------------
// CouponSortTypeEnum：排序方式（与后端枚举一致）
// ---------------------------------------------------------------------------
export const CouponSortTypeEnum = {
  /** 默认排序，即表中券的顺序 */
  DEFAULT: 'DEFAULT',
  /** 最新发布 */
  LATEST: 'LATEST',
  /** 即将过期 */
  EXPIRE_SOON: 'EXPIRE_SOON',
} as const

export type CouponSortTypeEnumCode =
  (typeof CouponSortTypeEnum)[keyof typeof CouponSortTypeEnum]

/**
 * 领券中心列表查询
 * （OpenAPI 文档名：`MiniCouponlQueryRequest`，路径 `POST /jdx/mini/promotion/couponPage`）
 */
export interface MiniCouponQueryRequest {
  /** 优惠券使用状态，参见 UserCouponStatusEnum，如 `['0']` */
  couponUseStatus?: UserCouponStatusEnumCode[]
  /** 商家名称（模糊匹配） */
  merchantName?: string
  /** 企业经营类型（商家认证），参见 CompanyBusinessTypeEnum */
  companyBusinessType?: CompanyBusinessTypeEnumCode | string
  /** 排序方式，参见 CouponSortTypeEnum */
  sortType?: CouponSortTypeEnumCode | string
  /** 页码，默认 1 */
  current?: number
  /** 每页大小，默认 10 */
  size?: number
}

/** 单条优惠券（OpenAPI: MiniCouponResponse） */
export interface MiniCouponResponse {
  /** 优惠券 ID */
  couponId: string
  /** 商家 ID */
  merchantId: string
  /** 商家名称 */
  merchantName: string
  /** 优惠券名称 */
  couponName: string
  /** 优惠券类型（1:平台券 2:自定义券） */
  couponType: string
  /** 优惠类型（1:折扣券 2:满减券） */
  discountType: string
  /** 优惠值（折扣率或满减金额） */
  discountValue: number
  /** 最低消费金额 */
  minConsume: number
  /** 最大优惠金额 */
  maxDiscount: number
  /** 每人限用次数 */
  limitPerUser: number
  /** 开始时间 */
  startTime: string
  /** 结束时间 */
  endTime: string
  /** 适用范围（1:全部门店 2:指定门店） */
  scopeType: number
  /** 说明（优惠券说明、使用条件等） */
  description: string
  /**
   * 是否已领取（当前买家维度）；true 已领取，false 未领取；
   * 超出券的领取次数时也返回 false
   */
  received: boolean
  receiveDetails?: any
}

/**
 * 买家侧列表（同一 `couponPage` 按状态筛选）时，单条可能额外带使用状态，
 * 用于「我的优惠券」全部 Tab；非 OpenAPI 领券中心 `MiniCouponResponse` 必填字段。
 */
export type BuyerMiniCouponResponse = MiniCouponResponse & {
  couponUseStatus?: UserCouponStatusEnumCode
}

/** MyBatis-Plus 分页排序项（OpenAPI: OrderItem） */
export interface OrderItem {
  column?: string
  asc?: boolean
}

/** 分页数据（OpenAPI: PageMiniCouponResponse） */
export interface PageMiniCouponResponse {
  /** 查询数据列表 */
  records: MiniCouponResponse[]
  /** 总数 */
  total: number
  /** 每页显示条数，默认 10 */
  size: number
  /** 当前页 */
  current: number
  /** 排序字段信息 */
  orders?: OrderItem[]
  /** 自动优化 COUNT SQL */
  optimizeCountSql?: boolean
  /** 是否进行 count 查询 */
  searchCount?: boolean
  /** optimizeJoinOfCountSql */
  optimizeJoinOfCountSql?: boolean
  /** 单页分页条数限制 */
  maxLimit?: number
  /** countId */
  countId?: string
}

/** 接口包装（OpenAPI: RPageMiniCouponResponse） */
export interface RPageMiniCouponResponse {
  code: number
  msg: string
  data: PageMiniCouponResponse
}

/**
 * 优惠券列表（领券中心）
 * POST `/jdx/mini/promotion/couponPage`（baseUrl 已含 `/jdx/mini`）
 */
/** 响应体经 axios 拦截器解包为 `{ code, msg, data }` */
export const apiCouponPage = (
  data: MiniCouponQueryRequest,
  config?: AxiosRequestConfig
): Promise<RPageMiniCouponResponse> => {
  return axios.post('/promotion/couponPage', data, config) as Promise<RPageMiniCouponResponse>
}

// ---------------------------------------------------------------------------
// 我的优惠券（OpenAPI: MiniMyCouponResponse / PageMiniMyCouponResponse）
// ---------------------------------------------------------------------------

/** 优惠券详情（OpenAPI: MiniCouponDetail） */
export interface MiniCouponDetail {
  couponId: string
  merchantId: string
  /** 商家名称（若后端在详情中返回） */
  merchantName?: string
  couponName: string
  description: string
  couponType: string
  discountType: string
  discountValue: number
  minConsume: number
  maxDiscount: number
  totalQuantity?: number
  limitPerUser: number
  startTime: string
  endTime: string
  /** 状态（0:有效 1:已过期 2:已停用）券定义维度 */
  status?: string
  channel?: string
  scopeType: number
}

/** 领取与使用详情（OpenAPI: MiniCouponReceiveDetail） */
export interface MiniCouponReceiveDetail {
  couponCode?: string
  /** 券的使用状态：0 未使用 / 1 已使用 / 2 已过期 */
  status?: string
  receiveTime?: string
  useTime?: string
  useStoreId?: string
  useStoreName?: string
}

/** 我的优惠券单条（OpenAPI: MiniMyCouponResponse） */
export interface MiniMyCouponResponse {
  couponDetail: MiniCouponDetail
  receiveDetail?: MiniCouponReceiveDetail
  merchantName?: string
}

/** 我的优惠券分页（OpenAPI: PageMiniMyCouponResponse） */
export interface PageMiniMyCouponResponse {
  records: MiniMyCouponResponse[]
  total: number
  size: number
  current: number
  orders?: OrderItem[]
  optimizeCountSql?: boolean
  searchCount?: boolean
  optimizeJoinOfCountSql?: boolean
  maxLimit?: number
  countId?: string
}

/** 我的优惠券接口包装（OpenAPI: RPageMiniMyCouponResponse） */
export interface RPageMiniMyCouponResponse {
  code: number
  msg: string
  data: PageMiniMyCouponResponse
}

/**
 * 我的优惠券列表
 * POST `/jdx/mini/promotion/myCouponPage`（baseUrl 已含 `/jdx/mini`）
 * 请求体同 MiniCouponlQueryRequest
 */
export const apiMyCouponPage = (
  data: MiniCouponQueryRequest,
  config?: AxiosRequestConfig
): Promise<RPageMiniMyCouponResponse> => {
  return axios.post('/promotion/myCouponPage', data, config) as Promise<RPageMiniMyCouponResponse>
}

/** 将 receiveDetail.status 转为 UserCouponStatusEnumCode */
export const parseReceiveDetailStatus = (
  status?: string
): UserCouponStatusEnumCode => {
  if (
    status === UserCouponStatusEnum.UNUSED ||
    status === UserCouponStatusEnum.USED ||
    status === UserCouponStatusEnum.EXPIRED
  ) {
    return status
  }
  return UserCouponStatusEnum.UNUSED
}

/** 领取优惠券请求（OpenAPI: MiniCouponReceiveRequest） */
export interface MiniCouponReceiveRequest {
  /** 优惠券 ID */
  couponId: string
  /** 门店 ID（扫描门店二维码领取时传） */
  storeId?: string
}

/**
 * 通用字符串/布尔 data 包装（OpenAPI: RString）
 * 文档示例中 `data` 可能为 `false`，故放宽类型
 */
export interface RStringResponse {
  code: number
  msg: string
  data?: string | boolean
}

/**
 * 领取优惠券
 * POST `/jdx/mini/promotion/receive`（baseUrl 已含 `/jdx/mini`）
 */
export const apiReceiveCoupon = (
  data: MiniCouponReceiveRequest,
  config?: AxiosRequestConfig
): Promise<RStringResponse> => {
  return axios.post('/promotion/receive', data, config) as Promise<RStringResponse>
}

/** 优惠类型：1 折扣券 2 满减券（与后端约定一致） */
export const CouponDiscountTypeEnum = {
  /** 折扣券 */
  DISCOUNT: '1',
  /** 满减券 */
  FULL_REDUCTION: '2',
} as const
